/**
 * Landing-page continuation from the hero booking demo.
 *
 * Reacts to the semantic `demo_opened` event emitted by HeroBookingFlowOriginal
 * rather than listening for raw pointer events on the document. It subscribes
 * via onTrackedEvent rather than PostHog's own capture pipeline, so the CTA
 * still appears on preview deployments, for internal browsers, and behind ad
 * blockers — all cases where PostHog drops the event before delivery.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { onTrackedEvent, trackEvent } from '@/lib/posthog';

const LANDING_PATH = '/';
const DISMISS_KEY = 'figgy_demo_cta_dismissed';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // Storage can be unavailable in privacy-restricted contexts.
    return false;
  }
}

export default function DemoContinuationCta() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => wasDismissed());
  // The path the demo was opened on. Visibility is derived from it, so
  // navigating away hides the CTA without resetting state from an effect.
  const [triggeredPath, setTriggeredPath] = useState<string | null>(null);
  const shownTracked = useRef(false);

  const onLanding = location.pathname === LANDING_PATH;
  const visible = onLanding && !dismissed && triggeredPath === LANDING_PATH;

  useEffect(() => {
    shownTracked.current = false;
    if (!onLanding || wasDismissed()) return;

    return onTrackedEvent((eventName) => {
      if (eventName !== 'demo_opened') return;
      setTriggeredPath(LANDING_PATH);
      if (!shownTracked.current) {
        shownTracked.current = true;
        trackEvent('demo_continue_shown', {
          cta_location: 'hero_demo',
          trigger_event: 'demo_opened',
        });
      }
    });
  }, [onLanding]);

  // The PWA install prompt docks to the same corner; it stands down while this
  // higher-intent CTA is showing (see the rule in index.css).
  useEffect(() => {
    if (!visible) return;
    document.body.setAttribute('data-demo-cta', 'visible');
    return () => document.body.removeAttribute('data-demo-cta');
  }, [visible]);

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Dismissal is best-effort — the in-memory dismiss still applies.
    }
  }

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Continue from the Figgy demo"
      className="fixed inset-x-4 bottom-[calc(16px+env(safe-area-inset-bottom))] z-[80] mx-auto grid
        max-w-[540px] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-3 rounded-[20px]
        bg-[rgba(18,16,25,.96)] p-3 shadow-[0_18px_50px_rgba(0,0,0,.32)] backdrop-blur-[14px]
        min-[460px]:grid-cols-[minmax(0,1fr)_auto_auto]"
    >
      <div className="min-w-0 text-white">
        <div className="text-[15px] font-extrabold">Make this experience yours.</div>
        <div className="mt-0.5 text-xs text-white/[.64]">
          Add your business, claim your link, and preview your page.
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss demo continuation"
        onClick={dismiss}
        className="col-start-2 row-start-1 h-9 w-9 shrink-0 cursor-pointer self-start rounded-full border-0
          bg-transparent p-0 text-xl leading-none text-white/[.65] min-[460px]:col-start-3 min-[460px]:self-center"
      >
        ×
      </button>
      <Link
        to="/magic-onboarding-v2?source=demo_continuation"
        onClick={() =>
          trackEvent('demo_continue_clicked', {
            cta_text: 'Make this my page',
            cta_location: 'hero_demo',
          })
        }
        className="col-span-2 row-start-2 whitespace-nowrap rounded-[13px] bg-figgy-500 px-3.5 py-3
          text-center text-[13px] font-extrabold text-white no-underline
          min-[460px]:col-span-1 min-[460px]:col-start-2 min-[460px]:row-start-1"
      >
        Make this my page →
      </Link>
    </div>
  );
}
